import { additionalOutParams, TDatasetName } from '@infotech-soe/metaschema';

import CachedSchemaRepository from '../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../infrastructure/schemas/storage/ISchemaRepository';
import CategoryViewModel from '../category/view-model/CategoryViewModel';
import CategoryViewModelSource from '../category/view-model/CategoryViewModelSource';
import DatasetViewModel from '../category/view-model/DatasetViewModel';
import { DatasetCurrentState } from '../dataset/view-model/DatasetCurrentState';
import { DatasetGridRow } from '../dataset/view-model/DatasetGridRow';
import UniqueId from '../uid/UniqueId';

import IDictionaryService, { DictionaryOutputs } from './IDictionaryService';

class DictionaryServiceSingleton implements IDictionaryService {
  private readonly schemaRepository: ISchemaRepository;
  constructor() {
    this.schemaRepository = new CachedSchemaRepository();
  }

  public async getOutputs(
    categoryState: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    multiSelect: boolean
  ): Promise<DictionaryOutputs | null> {
    return this.getDisplayModeOutputs(
      categoryState.source,
      categoryState.datasets,
      datasetsState,
      multiSelect
    );
  }

  private static getActiveRow(
    dataset: DatasetCurrentState
  ): DatasetGridRow | null {
    if (dataset.activeRowId === null) {
      return null;
    }

    for (const row of dataset.rows) {
      if (row[dataset.rowIdField] === dataset.activeRowId) {
        return row;
      }
    }

    return null;
  }

  private static getSelectedRows(
    dataset: DatasetCurrentState
  ): DatasetGridRow[] {
    return dataset.rows.filter(row =>
      dataset.selectedIds.includes(row[dataset.rowIdField])
    );
  }

  private async getDisplayModeOutputs(
    source: CategoryViewModelSource,
    datasets: Record<UniqueId, DatasetViewModel>,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    multiSelect: boolean
  ): Promise<DictionaryOutputs | null> {
    const schema = await this.schemaRepository.getDisplayMode(
      source.category,
      source.displayMode
    );

    if (!schema.outParams) {
      return null;
    }

    const datasetNames = Object.keys(schema.outParams)
      .map(key => schema.outParams?.[key].split('.')[0])
      .filter((v, i, a) => a.indexOf(v) === i) as TDatasetName[];
    const datasetRows: Record<TDatasetName, DatasetGridRow[]> = {};
    const datasetSelectedCounts: Record<TDatasetName, number> = {};
    let isRecordExists = false;
    datasetNames.forEach(datasetName => {
      const dataset =
        datasetsState[
          DictionaryServiceSingleton.findDataset(datasets, datasetName).id
        ];
      datasetRows[datasetName] = [];
      if (multiSelect && dataset.selectedIds.length) {
        datasetRows[datasetName] =
          DictionaryServiceSingleton.getSelectedRows(dataset);
      } else {
        const row = DictionaryServiceSingleton.getActiveRow(dataset);
        if (row) {
          datasetRows[datasetName] = row ? [row] : [];
        }
      }
      datasetSelectedCounts[datasetName] = dataset.selectedIds.length;
      isRecordExists = isRecordExists || datasetRows[datasetName].length > 0;
    });
    if (!isRecordExists) {
      return null;
    }
    const outputs: DictionaryOutputs = {};
    for (const outParam of Object.keys(schema.outParams)) {
      const path = schema.outParams[outParam];
      const [datasetName, fieldName] = path.split('.');

      if (fieldName === additionalOutParams.selectedCount) {
        outputs[outParam] = datasetSelectedCounts[datasetName];
      } else if (multiSelect) {
        outputs[outParam] = datasetRows[datasetName].map(row => row[fieldName]);
      } else {
        outputs[outParam] = datasetRows[datasetName].length
          ? datasetRows[datasetName][0][fieldName]
          : null;
      }
    }

    return outputs;
  }

  private static findDataset(
    datasets: Record<UniqueId, DatasetViewModel>,
    name: TDatasetName
  ): DatasetViewModel {
    for (const id of Object.keys(datasets)) {
      const dataset = datasets[id];

      if (dataset.name === name) {
        return dataset;
      }
    }

    throw new Error('No dataset ' + name);
  }
}

const DictionaryService = (() => {
  let instance: IDictionaryService;
  return new Proxy(DictionaryServiceSingleton, {
    construct(Target: typeof DictionaryServiceSingleton): IDictionaryService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default DictionaryService;
